import React, { useState, useEffect } from 'react';
import { useData } from '../context/DataContext';
import ChatWindow from './ChatWindow';

const WhatsAppWidget: React.FC = () => {
    const { settings } = useData();
    const [isVisible, setIsVisible] = useState(false);
    const [isChatOpen, setIsChatOpen] = useState(false);

    // Show widget after a brief delay for impact
    useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 1000);
        return () => clearTimeout(timer);
    }, []);

    // Check if enabled (default to true if config doesn't exist yet)
    const isEnabled = settings.whatsappConfig?.enabled ?? true;

    if (!isEnabled) return null;

    // Determine phone number to use
    const configPhone = settings.whatsappConfig?.phoneNumber;
    const rawPhone = configPhone && configPhone.length > 5 ? configPhone : settings.phoneNumber;

    // Format phone number for WhatsApp URL (remove non-digits)
    const phoneNumber = rawPhone?.replace(/\D/g, '');

    if (!phoneNumber) return null;

    // Get Custom Message
    const message = settings.whatsappConfig?.message || "Hello! I'm interested in learning more about your trucks.";

    // Tooltip visibility
    const showTooltip = settings.whatsappConfig?.showTooltip ?? true;

    // Check if Chatbot is enabled
    const isChatbotEnabled = settings.chatbotConfig?.enabled ?? true;

    const handleChatOpen = () => {
        if (!isChatbotEnabled) {
            // Direct WhatsApp Link if chatbot disabled
            const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
            window.open(url, '_blank');
        } else {
            setIsChatOpen(!isChatOpen);
        }
    };

    return (
        <>
            <style>
                {`
          @keyframes gentle-pulse {
            0% { box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.7); }
            70% { box-shadow: 0 0 0 15px rgba(37, 211, 102, 0); }
            100% { box-shadow: 0 0 0 0 rgba(37, 211, 102, 0); }
          }
          .whatsapp-pulse {
            animation: gentle-pulse 2s infinite;
          }
        `}
            </style>

            {/* Chat Window Component - Rendered always if enabled to preserve state */}
            {isChatbotEnabled && (
                <ChatWindow
                    isOpen={isChatOpen}
                    onClose={() => setIsChatOpen(false)}
                    phoneNumber={phoneNumber}
                    whatsappMessage={message}
                />
            )}

            <div
                className={`fixed bottom-6 right-6 z-50 transition-all duration-500 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                    }`}
            >
                <button
                    onClick={handleChatOpen}
                    className="group relative flex items-center justify-center w-14 h-14 bg-[#25D366] rounded-full shadow-lg hover:scale-110 transition-transform duration-300 whatsapp-pulse"
                    aria-label="Chat on WhatsApp"
                >
                    {/* Tooltip */}
                    {showTooltip && (
                        <span className="absolute right-full mr-4 bg-white text-gray-800 px-3 py-1.5 rounded-lg text-sm font-semibold shadow-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                            Chat with AI Assistant
                            {/* Tiny arrow */}
                            <span className="absolute right-[-4px] top-1/2 transform -translate-y-1/2 w-2 h-2 bg-white rotate-45"></span>
                        </span>
                    )}

                    {/* WhatsApp Icon SVG */}
                    {isChatOpen ? (
                        /* Close Icon */
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    ) : (
                        /* WhatsApp Icon */
                        <svg
                            className="w-8 h-8 text-white fill-current"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M12.0117 2C6.50574 2 2.02344 6.47837 2.02344 11.9841C2.02344 13.7485 2.48166 15.463 3.37682 16.9926L2 22L7.15282 20.6508C8.61808 21.4485 10.2831 21.8683 11.9866 21.8683C17.4925 21.8683 21.9749 17.39 21.9749 11.8841C21.9749 6.37837 17.4939 2 12.0117 2ZM12.0089 20.19C10.5108 20.19 9.07185 19.7824 7.82885 19.0433L7.54013 18.8715L4.47192 19.6756L5.29063 16.6853L5.00693 16.2335C4.20921 14.9654 3.7885 13.4839 3.7885 11.9827C3.7885 7.4529 7.47648 3.76637 12.0104 3.76637C16.5442 3.76637 20.2104 7.4529 20.2104 11.8827C20.2104 16.3125 16.5331 20.19 12.0089 20.19ZM16.0354 14.2882C15.8143 14.1776 14.7308 13.6445 14.5298 13.5714C14.3275 13.4984 14.1793 13.4616 14.0326 13.6826C13.8844 13.9037 13.461 14.3995 13.3323 14.5471C13.2035 14.6947 13.0747 14.7121 12.8537 14.6014C12.6326 14.4909 11.9211 14.2575 11.0762 13.5042C10.4077 12.9082 9.9566 12.1723 9.82793 11.9512C9.69919 11.7302 9.81432 11.6111 9.925 11.5015C10.0237 11.4036 10.1456 11.2468 10.2562 11.118C10.3668 10.9892 10.4037 10.897 10.4774 10.7494C10.5511 10.6019 10.5143 10.4731 10.459 10.3625C10.4037 10.2519 9.96195 9.16527 9.77812 8.72314C9.59385 8.28096 9.40955 8.34139 9.27364 8.34139C9.14172 8.34139 8.99411 8.33232 8.84649 8.33232C8.69888 8.33232 8.45914 8.38761 8.25622 8.60871C8.0533 8.82975 7.48154 9.3638 7.48154 10.4504C7.48154 11.537 8.27464 12.5878 8.38528 12.7352C8.49586 12.8827 9.93299 15.0934 12.1332 16.0335C12.6567 16.2571 13.0649 16.3904 13.3859 16.4921C13.9142 16.6596 14.4025 16.6359 14.7891 16.5786C15.2223 16.5143 16.1275 16.0315 16.3118 15.5159C16.4961 15.0003 16.4961 14.5583 16.4409 14.466C16.3856 14.3738 16.2379 14.3251 16.017 14.2145L16.0354 14.2882Z" />
                        </svg>
                    )}
                </button>
            </div>
        </>
    );
};

export default WhatsAppWidget;
